import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Switch } from '../components/ui/switch';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import {
  Contact, Plus, Trash2, Pencil, Search, Loader2, AlertTriangle, CalendarClock, CreditCard, Ban, RotateCcw, FileDown, FileText, FileSpreadsheet, Mail,
} from 'lucide-react';
import axios from 'axios';
import { toast } from '../hooks/use-toast';

const API = process.env.REACT_APP_BACKEND_URL;

const EMPTY = {
  name: '', nationality: '', visa_company: '', labour_contract_salary: '', insurance_cost: '',
  emirates_id_no: '', passport_no: '', passport_expiry_date: '',
  visa_start_date: '', visa_expiry_date: '', visa_reference: '', approval_of: '',
  labour_card_expiry_date: '',
  insurance_category: '', insurance_expiry_date: '', wps_card_no: '', bank_details: '',
};

const money = (v) => (v || v === 0) ? Number(v).toLocaleString() : '—';

const fmt = (iso) => {
  if (!iso) return '—';
  try { return new Date(iso + 'T00:00:00').toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }); }
  catch { return iso; }
};

// 'expired' | 'soon' (<=30d) | 'ok' | null
const expiryStatus = (iso) => {
  if (!iso) return null;
  const today = new Date(); today.setHours(0, 0, 0, 0);
  const d = new Date(iso + 'T00:00:00');
  if (isNaN(d)) return null;
  const days = Math.ceil((d - today) / 86400000);
  if (days < 0) return 'expired';
  if (days <= 30) return 'soon';
  return 'ok';
};

const ExpiryCell = ({ iso }) => {
  const s = expiryStatus(iso);
  const cls = s === 'expired' ? 'text-red-600 font-semibold'
    : s === 'soon' ? 'text-amber-600 font-semibold' : 'text-gray-700';
  return (
    <span className={cls}>
      {fmt(iso)}
      {s === 'expired' && <Badge variant="outline" className="ml-1 text-[9px] text-red-600 border-red-300 py-0">Expired</Badge>}
      {s === 'soon' && <Badge variant="outline" className="ml-1 text-[9px] text-amber-600 border-amber-300 py-0">Soon</Badge>}
    </span>
  );
};

const currentMonth = () => new Date().toISOString().slice(0, 7);
const monthLabel = (m) => {
  try { return new Date(m + '-01T00:00:00').toLocaleDateString('en-GB', { month: 'short', year: 'numeric' }); }
  catch { return m; }
};

export default function VisaHolders() {
  const token = localStorage.getItem('token');
  const headers = { Authorization: `Bearer ${token}` };

  const [holders, setHolders] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [companySummary, setCompanySummary] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [expiringOnly, setExpiringOnly] = useState(false);
  const [month, setMonth] = useState(currentMonth());

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [cancelTarget, setCancelTarget] = useState(null);
  const [cancelDate, setCancelDate] = useState('');
  const [cancelReason, setCancelReason] = useState('');
  const [cancelReasonPreset, setCancelReasonPreset] = useState('');
  const [cancelling, setCancelling] = useState(false);
  const [hideCancelled, setHideCancelled] = useState(false);
  const [reportDialogOpen, setReportDialogOpen] = useState(false);
  const [reportStart, setReportStart] = useState('');
  const [reportEnd, setReportEnd] = useState('');
  const [reportRecipients, setReportRecipients] = useState('');
  const [emailing, setEmailing] = useState(false);
  const [recipientGroups, setRecipientGroups] = useState([]);

  const load = useCallback(() => {
    setLoading(true);
    axios.get(`${API}/api/visa-holders`, { headers })
      .then(r => setHolders(r.data || []))
      .catch(() => toast({ title: 'Failed to load visa holders', variant: 'destructive' }))
      .finally(() => setLoading(false));
    axios.get(`${API}/api/visa-holders/summary/by-company`, { headers })
      .then(r => setCompanySummary(r.data?.rows || []))
      .catch(() => {});
    axios.get(`${API}/api/settings/wps`, { headers })
      .then(r => setCompanies(r.data?.visa_companies || []))
      .catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => { load(); }, [load]);

  const openAdd = () => { setEditing(null); setForm(EMPTY); setDialogOpen(true); };
  const openEdit = (h) => {
    setEditing(h.id);
    setForm({
      name: h.name || '', nationality: h.nationality || '',
      visa_company: h.visa_company || '',
      labour_contract_salary: h.labour_contract_salary ?? '', insurance_cost: h.insurance_cost ?? '',
      emirates_id_no: h.emirates_id_no || '',
      passport_no: h.passport_no || '', passport_expiry_date: h.passport_expiry_date || '',
      visa_start_date: h.visa_start_date || '', visa_expiry_date: h.visa_expiry_date || '',
      visa_reference: h.visa_reference || '', approval_of: h.approval_of || '',
      labour_card_expiry_date: h.labour_card_expiry_date || '', insurance_category: h.insurance_category || '',
      insurance_expiry_date: h.insurance_expiry_date || '', wps_card_no: h.wps_card_no || '', bank_details: h.bank_details || '',
    });
    setDialogOpen(true);
  };

  const save = async () => {
    if (!form.name.trim()) { toast({ title: 'Name is required', variant: 'destructive' }); return; }
    setSaving(true);
    const payload = {
      ...form,
      labour_contract_salary: form.labour_contract_salary === '' ? null : parseFloat(form.labour_contract_salary),
      insurance_cost: form.insurance_cost === '' ? null : parseFloat(form.insurance_cost),
    };
    try {
      if (editing) await axios.put(`${API}/api/visa-holders/${editing}`, payload, { headers });
      else await axios.post(`${API}/api/visa-holders`, payload, { headers });
      toast({ title: editing ? 'Visa holder updated' : 'Visa holder added' });
      setDialogOpen(false);
      load();
    } catch (e) {
      toast({ title: 'Failed', description: e?.response?.data?.detail || '', variant: 'destructive' });
    } finally { setSaving(false); }
  };

  const remove = async (h) => {
    if (!window.confirm(`Delete visa holder "${h.name}"?`)) return;
    try {
      await axios.delete(`${API}/api/visa-holders/${h.id}`, { headers });
      toast({ title: 'Deleted' });
      load();
    } catch { toast({ title: 'Failed to delete', variant: 'destructive' }); }
  };

  const openCancel = (h) => { setCancelTarget(h); setCancelDate(new Date().toISOString().slice(0, 10)); setCancelReason(''); setCancelReasonPreset(''); };
  const confirmCancel = async () => {
    if (!cancelDate) { toast({ title: 'Pick a cancellation date', variant: 'destructive' }); return; }
    if (!cancelReasonPreset) { toast({ title: 'Select a reason', variant: 'destructive' }); return; }
    if (cancelReasonPreset === 'Other' && !cancelReason.trim()) { toast({ title: 'Enter the reason', variant: 'destructive' }); return; }
    const finalReason = cancelReasonPreset === 'Other' ? cancelReason : cancelReasonPreset;
    setCancelling(true);
    try {
      await axios.put(`${API}/api/visa-holders/${cancelTarget.id}/cancel`, { cancellation_date: cancelDate, cancellation_reason: finalReason }, { headers });
      toast({ title: 'Visa marked cancelled' });
      setCancelTarget(null);
      load();
    } catch { toast({ title: 'Failed to cancel', variant: 'destructive' }); }
    finally { setCancelling(false); }
  };
  const uncancel = async (h) => {
    if (!window.confirm(`Restore "${h.name}" as active?`)) return;
    try {
      await axios.put(`${API}/api/visa-holders/${h.id}/uncancel`, {}, { headers });
      toast({ title: 'Restored to active' });
      load();
    } catch { toast({ title: 'Failed to restore', variant: 'destructive' }); }
  };

  const toggleWps = async (h, processed) => {
    // optimistic
    setHolders(prev => prev.map(x => x.id === h.id
      ? { ...x, wps_status: { ...(x.wps_status || {}), [month]: processed } } : x));
    try {
      await axios.put(`${API}/api/visa-holders/${h.id}/wps`, { month, processed }, { headers });
    } catch {
      toast({ title: 'Failed to update WPS', variant: 'destructive' });
      load();
    }
  };

  const filtered = useMemo(() => {
    let list = holders;
    if (hideCancelled) list = list.filter(h => !h.cancelled);
    if (search.trim()) {
      const q = search.toLowerCase();
      list = list.filter(h => (h.name || '').toLowerCase().includes(q) || (h.wps_card_no || '').toLowerCase().includes(q));
    }
    if (expiringOnly) {
      list = list.filter(h => ['expired', 'soon'].includes(expiryStatus(h.visa_expiry_date))
        || ['expired', 'soon'].includes(expiryStatus(h.labour_card_expiry_date))
        || ['expired', 'soon'].includes(expiryStatus(h.insurance_expiry_date)));
    }
    return list;
  }, [holders, search, expiringOnly, hideCancelled]);

  const cancelledCount = useMemo(() => holders.filter(h => h.cancelled).length, [holders]);

  const openReportDialog = async () => {
    setReportStart(''); setReportEnd('');
    setReportDialogOpen(true);
    try {
      const r = await axios.get(`${API}/api/visa-holders/cancelled/report/recipients`, { headers });
      setReportRecipients(r.data?.recipients || '');
    } catch { /* ignore */ }
    try {
      const g = await axios.get(`${API}/api/scheduled-reports/recipient-groups`, { headers });
      setRecipientGroups(g.data?.groups || []);
    } catch { /* ignore */ }
  };

  const applyGroupToEmail = (g) => {
    setReportRecipients((prev) => {
      const existing = prev.split(/[,;\s]+/).map((x) => x.trim().toLowerCase()).filter(Boolean);
      const add = g.emails.filter((e) => !existing.includes(e.toLowerCase()));
      const merged = [...prev.split(/[,;]+/).map((x) => x.trim()).filter(Boolean), ...add];
      return merged.join(', ');
    });
  };

  const downloadCancelledReport = async (format) => {
    try {
      const res = await axios.get(`${API}/api/visa-holders/cancelled/report`, {
        params: { format, start_date: reportStart || undefined, end_date: reportEnd || undefined },
        headers,
        responseType: 'blob',
      });
      const isPdf = format === 'pdf';
      const blob = new Blob([res.data], {
        type: isPdf ? 'application/pdf' : 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `Cancelled_Visa_Report.${isPdf ? 'pdf' : 'xlsx'}`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      toast({ title: 'Export failed', description: 'Could not generate the cancelled visa report.', variant: 'destructive' });
    }
  };

  const emailCancelledReport = async () => {
    if (!reportRecipients.trim()) { toast({ title: 'Add at least one recipient email', variant: 'destructive' }); return; }
    setEmailing(true);
    try {
      const res = await axios.post(`${API}/api/visa-holders/cancelled/report/email`, {
        recipients: reportRecipients,
        start_date: reportStart || null,
        end_date: reportEnd || null,
      }, { headers });
      toast({ title: 'Report emailed', description: `Sent ${res.data?.count ?? ''} record(s) to ${res.data?.sent_to?.join(', ')}` });
      setReportDialogOpen(false);
    } catch (e) {
      toast({ title: 'Email failed', description: e?.response?.data?.detail || 'Could not send the report email.', variant: 'destructive' });
    } finally {
      setEmailing(false);
    }
  };

  const expiringCount = useMemo(() => holders.filter(h =>
    ['expired', 'soon'].includes(expiryStatus(h.visa_expiry_date))
    || ['expired', 'soon'].includes(expiryStatus(h.labour_card_expiry_date))
    || ['expired', 'soon'].includes(expiryStatus(h.insurance_expiry_date))).length, [holders]);

  return (
    <div className="p-6 w-full space-y-4" data-testid="visa-holders-page">
      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-2">
            <Contact className="h-7 w-7 text-violet-600" /> Visa Holders
          </h1>
          <p className="text-gray-600 mt-1 text-sm">
            Non-employee visa holders sponsored on the company's visa — visa, labour card, insurance &amp; WPS tracking.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" className="border-rose-200 text-rose-700 hover:bg-rose-50" onClick={openReportDialog} data-testid="vh-cancelled-report-btn">
            <FileDown className="h-4 w-4 mr-2" /> Cancelled Report
            {cancelledCount > 0 && <Badge className="ml-2 bg-rose-100 text-rose-700">{cancelledCount}</Badge>}
          </Button>
          <Button onClick={openAdd} className="bg-violet-600 hover:bg-violet-700" data-testid="vh-add-btn">
            <Plus className="h-4 w-4 mr-2" /> Add visa holder
          </Button>
        </div>
      </div>

      {/* Controls */}
      <Card><CardContent className="p-3 flex flex-wrap items-center gap-3">
        <div className="relative flex-1 min-w-[200px]">
          <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-gray-400" />
          <Input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search name or WPS card…" className="pl-8" data-testid="vh-search" />
        </div>
        <div className="flex items-center gap-2">
          <Label className="text-xs text-gray-500">WPS month</Label>
          <Input type="month" value={month} onChange={e => setMonth(e.target.value)} className="w-40" data-testid="vh-month" />
        </div>
        <label className="flex items-center gap-2 text-sm cursor-pointer">
          <Switch checked={expiringOnly} onCheckedChange={setExpiringOnly} data-testid="vh-expiring-filter" />
          <span className="flex items-center gap-1"><AlertTriangle className="h-3.5 w-3.5 text-amber-500" /> Expiring soon
            {expiringCount > 0 && <Badge className="bg-amber-100 text-amber-700">{expiringCount}</Badge>}</span>
        </label>
        <label className="flex items-center gap-2 text-sm cursor-pointer">
          <Switch checked={hideCancelled} onCheckedChange={setHideCancelled} data-testid="vh-hide-cancelled-filter" />
          <span className="flex items-center gap-1"><Ban className="h-3.5 w-3.5 text-rose-500" /> Hide cancelled
            {cancelledCount > 0 && <Badge className="bg-rose-100 text-rose-700">{cancelledCount}</Badge>}</span>
        </label>
      </CardContent></Card>

      {/* Per-company visa count: staff + holders */}
      {companySummary.length > 0 && (
        <div className="flex flex-wrap gap-2" data-testid="vh-company-summary">
          {companySummary.map(c => (
            <div key={c.company} className="bg-white border rounded-lg px-3 py-2 text-sm shadow-sm" data-testid={`vh-summary-${c.company}`}>
              <span className="font-semibold text-gray-800">{c.company}</span>
              <span className="text-gray-500"> · {c.staff} staff</span>
              {c.holders > 0 && <span className="text-violet-600 font-medium"> + {c.holders} holder{c.holders > 1 ? 's' : ''}</span>}
              <span className="ml-1.5 font-bold text-gray-900">= {c.total}</span>
            </div>
          ))}
        </div>
      )}

      {/* Table */}
      <Card><CardContent className="p-0 overflow-x-auto">
        {loading ? (
          <div className="p-8 text-center text-gray-500 flex items-center justify-center gap-2"><Loader2 className="h-4 w-4 animate-spin" /> Loading…</div>
        ) : filtered.length === 0 ? (
          <div className="p-10 text-center text-gray-400" data-testid="vh-empty">
            <Contact className="h-8 w-8 mx-auto mb-2 text-gray-300" />
            {holders.length === 0 ? 'No visa holders yet. Add your first one.' : 'No matches.'}
          </div>
        ) : (
          <table className="w-full text-sm whitespace-nowrap">
            <thead className="bg-gray-50 text-xs uppercase text-gray-600">
              <tr>
                <th className="text-left p-3">Name</th>
                <th className="text-left p-3">Sponsor (Visa)</th>
                <th className="text-left p-3">Nationality</th>
                <th className="text-right p-3">Contract Salary</th>
                <th className="text-right p-3">Insurance Cost</th>
                <th className="text-left p-3">Emirates ID</th>
                <th className="text-left p-3">Passport / expiry</th>
                <th className="text-left p-3">Visa start</th>
                <th className="text-left p-3">Visa expiry</th>
                <th className="text-left p-3">Reference / Approval</th>
                <th className="text-left p-3">Labour card expiry</th>
                <th className="text-left p-3">Insurance</th>
                <th className="text-left p-3">Insurance expiry</th>
                <th className="text-left p-3">WPS Card / Bank</th>
                <th className="text-left p-3">WPS {monthLabel(month)}</th>
                <th className="text-right p-3">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map(h => {
                const wps = !!(h.wps_status || {})[month];
                return (
                  <tr key={h.id} className={`border-t hover:bg-gray-50 ${h.cancelled ? 'bg-rose-50/40 text-gray-400 [&_td]:line-through' : ''}`} data-testid={`vh-row-${h.id}`}>
                    <td className="p-3 font-medium text-gray-900">
                      {h.name}
                      {h.cancelled && (
                        <div className="no-underline">
                          <Badge variant="outline" className="ml-2 text-[9px] text-rose-600 border-rose-300 no-underline align-middle" data-testid={`vh-cancelled-badge-${h.id}`}>
                            Visa cancelled {fmt(h.cancellation_date)}
                          </Badge>
                          {h.cancellation_reason && (
                            <span className="block text-[10px] text-gray-400 no-underline mt-0.5">Reason: {h.cancellation_reason}</span>
                          )}
                        </div>
                      )}
                    </td>
                    <td className="p-3">
                      {h.visa_company
                        ? <Badge variant="outline" className="border-violet-300 text-violet-700">{h.visa_company}</Badge>
                        : <span className="text-gray-400">—</span>}
                    </td>
                    <td className="p-3 text-gray-700">{h.nationality || <span className="text-gray-400">—</span>}</td>
                    <td className="p-3 text-right font-mono text-gray-700">{money(h.labour_contract_salary)}</td>
                    <td className="p-3 text-right font-mono text-emerald-700">{money(h.insurance_cost)}</td>
                    <td className="p-3 text-xs text-gray-600">{h.emirates_id_no || <span className="text-gray-400">—</span>}</td>
                    <td className="p-3 text-xs text-gray-600">
                      {h.passport_no && <div>{h.passport_no}</div>}
                      {h.passport_expiry_date ? <ExpiryCell iso={h.passport_expiry_date} /> : (!h.passport_no && <span className="text-gray-400">—</span>)}
                    </td>
                    <td className="p-3 text-gray-700">{fmt(h.visa_start_date)}</td>
                    <td className="p-3"><ExpiryCell iso={h.visa_expiry_date} /></td>
                    <td className="p-3 text-xs text-gray-600">
                      {h.visa_reference && <div><span className="text-gray-400">Ref:</span> {h.visa_reference}</div>}
                      {h.approval_of && <div><span className="text-gray-400">Approval:</span> {h.approval_of}</div>}
                      {!h.visa_reference && !h.approval_of && <span className="text-gray-400">—</span>}
                    </td>
                    <td className="p-3"><ExpiryCell iso={h.labour_card_expiry_date} /></td>
                    <td className="p-3">
                      {h.insurance_category
                        ? <Badge variant="outline" className={h.insurance_category === 'A' ? 'border-violet-300 text-violet-700' : 'border-sky-300 text-sky-700'}>Cat {h.insurance_category}</Badge>
                        : <span className="text-gray-400">—</span>}
                    </td>
                    <td className="p-3"><ExpiryCell iso={h.insurance_expiry_date} /></td>
                    <td className="p-3 text-xs text-gray-600">
                      {h.wps_card_no && <div className="flex items-center gap-1"><CreditCard className="h-3 w-3 text-gray-400" /> {h.wps_card_no}</div>}
                      {h.bank_details && <div className="text-gray-500">{h.bank_details}</div>}
                      {!h.wps_card_no && !h.bank_details && <span className="text-gray-400">—</span>}
                    </td>
                    <td className="p-3">
                      <div className="flex items-center gap-2">
                        <Switch checked={wps} onCheckedChange={(v) => toggleWps(h, v)} data-testid={`vh-wps-${h.id}`} />
                        <span className={`text-xs font-medium ${wps ? 'text-emerald-600' : 'text-gray-400'}`}>{wps ? 'Processed' : 'Not yet'}</span>
                      </div>
                    </td>
                    <td className="p-3 text-right whitespace-nowrap no-underline [&_*]:no-underline">
                      <Button size="sm" variant="ghost" className="h-8 text-gray-500 hover:text-violet-600" onClick={() => openEdit(h)} data-testid={`vh-edit-${h.id}`}><Pencil className="h-4 w-4" /></Button>
                      {h.cancelled ? (
                        <Button size="sm" variant="ghost" className="h-8 text-gray-500 hover:text-emerald-600" onClick={() => uncancel(h)} title="Restore to active" data-testid={`vh-uncancel-${h.id}`}><RotateCcw className="h-4 w-4" /></Button>
                      ) : (
                        <Button size="sm" variant="ghost" className="h-8 text-gray-500 hover:text-rose-600" onClick={() => openCancel(h)} title="Mark visa cancelled" data-testid={`vh-cancel-${h.id}`}><Ban className="h-4 w-4" /></Button>
                      )}
                      <Button size="sm" variant="ghost" className="h-8 text-gray-500 hover:text-red-600" onClick={() => remove(h)} data-testid={`vh-delete-${h.id}`}><Trash2 className="h-4 w-4" /></Button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </CardContent></Card>

      <p className="text-[11px] text-gray-400 px-1 flex items-center gap-1">
        <CalendarClock className="h-3 w-3" /> Rows highlight in amber within 30 days of expiry and red once expired. WPS status is tracked per month — change the "WPS month" above to mark other months.
      </p>

      {/* Add / Edit dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-lg" data-testid="vh-dialog">
          <DialogHeader>
            <DialogTitle>{editing ? 'Edit visa holder' : 'Add visa holder'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3 max-h-[60vh] overflow-y-auto pr-1">
            <div>
              <Label className="text-xs">Name *</Label>
              <Input value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} data-testid="vh-form-name" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-xs">Nationality</Label>
                <Input value={form.nationality} onChange={e => setForm(f => ({ ...f, nationality: e.target.value }))} placeholder="e.g. Indian" data-testid="vh-form-nationality" /></div>
              <div><Label className="text-xs">Emirates ID No.</Label>
                <Input value={form.emirates_id_no} onChange={e => setForm(f => ({ ...f, emirates_id_no: e.target.value }))} placeholder="784-XXXX-XXXXXXX-X" data-testid="vh-form-eid" /></div>
            </div>
            <div className="grid grid-cols-3 gap-3 bg-violet-50 border border-violet-100 rounded-lg p-3">
              <div><Label className="text-xs">Sponsored under (Visa Company)</Label>
                <select value={form.visa_company} onChange={e => setForm(f => ({ ...f, visa_company: e.target.value }))}
                  className="w-full h-10 px-2 border rounded-md text-sm bg-white" data-testid="vh-form-visa-company">
                  <option value="">— Select —</option>
                  {companies.map(c => <option key={c} value={c}>{c}</option>)}
                  {form.visa_company && !companies.includes(form.visa_company) && <option value={form.visa_company}>{form.visa_company}</option>}
                </select></div>
              <div><Label className="text-xs">Labour Contract Salary (AED)</Label>
                <Input type="number" min={0} value={form.labour_contract_salary} onChange={e => setForm(f => ({ ...f, labour_contract_salary: e.target.value }))} placeholder="e.g. 3500" className="bg-white" data-testid="vh-form-contract-salary" /></div>
              <div><Label className="text-xs">Insurance Cost / yr (AED)</Label>
                <Input type="number" min={0} value={form.insurance_cost} onChange={e => setForm(f => ({ ...f, insurance_cost: e.target.value }))} placeholder="e.g. 1200" className="bg-white" data-testid="vh-form-insurance-cost" /></div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-xs">Passport No.</Label>
                <Input value={form.passport_no} onChange={e => setForm(f => ({ ...f, passport_no: e.target.value }))} placeholder="Passport number" data-testid="vh-form-passport-no" /></div>
              <div><Label className="text-xs">Passport expiry date</Label>
                <Input type="date" value={form.passport_expiry_date} onChange={e => setForm(f => ({ ...f, passport_expiry_date: e.target.value }))} data-testid="vh-form-passport-expiry" /></div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-xs">Visa start date</Label>
                <Input type="date" value={form.visa_start_date} onChange={e => setForm(f => ({ ...f, visa_start_date: e.target.value }))} data-testid="vh-form-visa-start" /></div>
              <div><Label className="text-xs">Visa expiry date</Label>
                <Input type="date" value={form.visa_expiry_date} onChange={e => setForm(f => ({ ...f, visa_expiry_date: e.target.value }))} data-testid="vh-form-visa-expiry" /></div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-xs">Visa added under (reference / relation to)</Label>
                <Input value={form.visa_reference} onChange={e => setForm(f => ({ ...f, visa_reference: e.target.value }))} placeholder="Whose reference / relation" data-testid="vh-form-visa-reference" /></div>
              <div><Label className="text-xs">Approval of</Label>
                <Input value={form.approval_of} onChange={e => setForm(f => ({ ...f, approval_of: e.target.value }))} placeholder="Approved by" data-testid="vh-form-approval" /></div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-xs">Labour card expiry</Label>
                <Input type="date" value={form.labour_card_expiry_date} onChange={e => setForm(f => ({ ...f, labour_card_expiry_date: e.target.value }))} data-testid="vh-form-labour-expiry" /></div>
              <div><Label className="text-xs">Insurance category</Label>
                <select value={form.insurance_category} onChange={e => setForm(f => ({ ...f, insurance_category: e.target.value }))}
                  className="w-full h-10 px-2 border rounded-md text-sm" data-testid="vh-form-category">
                  <option value="">—</option>
                  <option value="A">A</option>
                  <option value="B">B</option>
                </select></div>
            </div>
            <div>
              <Label className="text-xs">Insurance expiry date</Label>
              <Input type="date" value={form.insurance_expiry_date} onChange={e => setForm(f => ({ ...f, insurance_expiry_date: e.target.value }))} data-testid="vh-form-insurance-expiry" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-xs">WPS Card No.</Label>
                <Input value={form.wps_card_no} onChange={e => setForm(f => ({ ...f, wps_card_no: e.target.value }))} placeholder="WPS card number" data-testid="vh-form-wps-card" /></div>
              <div><Label className="text-xs">Bank / IBAN details</Label>
                <Input value={form.bank_details} onChange={e => setForm(f => ({ ...f, bank_details: e.target.value }))} placeholder="Bank account / IBAN" data-testid="vh-form-bank" /></div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
            <Button onClick={save} disabled={saving} className="bg-violet-600 hover:bg-violet-700" data-testid="vh-save">
              {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null}{editing ? 'Save changes' : 'Add'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Cancelled report dialog: date range + download + email */}
      <Dialog open={reportDialogOpen} onOpenChange={setReportDialogOpen}>
        <DialogContent className="max-w-md" data-testid="vh-report-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><FileDown className="h-5 w-5 text-rose-600" /> Cancelled Visa Report</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-sm text-gray-600">
              Exports all cancelled visas (register + employees cancelled on exit). Leave dates blank for all-time.
            </p>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">From (optional)</Label>
                <Input type="date" value={reportStart} onChange={e => setReportStart(e.target.value)} data-testid="vh-report-start" />
              </div>
              <div>
                <Label className="text-xs">To (optional)</Label>
                <Input type="date" value={reportEnd} onChange={e => setReportEnd(e.target.value)} data-testid="vh-report-end" />
              </div>
            </div>
            <div className="flex gap-2">
              <Button variant="outline" className="flex-1 border-rose-200 text-rose-700 hover:bg-rose-50" onClick={() => downloadCancelledReport('pdf')} data-testid="vh-report-download-pdf">
                <FileText className="h-4 w-4 mr-2" /> PDF
              </Button>
              <Button variant="outline" className="flex-1 border-emerald-200 text-emerald-700 hover:bg-emerald-50" onClick={() => downloadCancelledReport('excel')} data-testid="vh-report-download-excel">
                <FileSpreadsheet className="h-4 w-4 mr-2" /> Excel
              </Button>
            </div>
            <div className="border-t pt-3">
              <Label className="text-xs">Email report to (PRO / Finance)</Label>
              <Input
                value={reportRecipients}
                onChange={e => setReportRecipients(e.target.value)}
                placeholder="pro@company.com, finance@company.com"
                data-testid="vh-report-recipients"
              />
              <p className="text-[11px] text-gray-400 mt-1">Recipients are saved as the default for next time. PDF + Excel are attached.</p>
              {recipientGroups.length > 0 && (
                <div className="flex flex-wrap items-center gap-1.5 mt-2">
                  <span className="text-[11px] text-gray-500">Groups:</span>
                  {recipientGroups.map((g) => (
                    <button
                      key={g.name}
                      type="button"
                      onClick={() => applyGroupToEmail(g)}
                      className="bg-emerald-50 text-emerald-700 text-[11px] rounded-full px-2 py-0.5 hover:bg-emerald-100"
                      data-testid={`email-group-${g.name}`}
                    >
                      + {g.name} ({g.emails.length})
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setReportDialogOpen(false)}>Close</Button>
            <Button onClick={emailCancelledReport} disabled={emailing} className="bg-rose-600 hover:bg-rose-700" data-testid="vh-report-email-btn">
              {emailing ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Mail className="h-4 w-4 mr-2" />} Email report
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Cancel visa dialog */}
      <Dialog open={!!cancelTarget} onOpenChange={(o) => !o && setCancelTarget(null)}>
        <DialogContent className="max-w-sm" data-testid="vh-cancel-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Ban className="h-5 w-5 text-rose-600" /> Mark visa cancelled</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <p className="text-sm text-gray-600">
              This keeps <strong>{cancelTarget?.name}</strong>'s record but strikes out the line and marks it cancelled. You can restore it later.
            </p>
            <div>
              <Label className="text-xs">Date of cancellation</Label>
              <Input type="date" value={cancelDate} onChange={e => setCancelDate(e.target.value)} data-testid="vh-cancel-date" />
            </div>
            <div>
              <Label className="text-xs">Reason</Label>
              <select
                value={cancelReasonPreset}
                onChange={(e) => { setCancelReasonPreset(e.target.value); if (e.target.value !== 'Other') setCancelReason(''); }}
                className="w-full h-10 px-2 border rounded-md text-sm"
                data-testid="vh-cancel-reason-preset"
              >
                <option value="">Select a reason…</option>
                <option value="Returned home country">Returned home country</option>
                <option value="Transferred to new sponsor">Transferred to new sponsor</option>
                <option value="Absconded">Absconded</option>
                <option value="Other">Other</option>
              </select>
              {cancelReasonPreset === 'Other' && (
                <Input
                  className="mt-2"
                  value={cancelReason}
                  onChange={e => setCancelReason(e.target.value)}
                  placeholder="Enter reason"
                  data-testid="vh-cancel-reason"
                />
              )}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCancelTarget(null)}>Cancel</Button>
            <Button onClick={confirmCancel} disabled={cancelling} className="bg-rose-600 hover:bg-rose-700" data-testid="vh-cancel-confirm">
              {cancelling ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null}Mark cancelled
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
